import * as React from "react";

import { useSelector, useDispatch } from "react-redux";

// material ui
import { makeStyles } from "@material-ui/styles";
import Divider from "@material-ui/core/Divider";
import Card from "@material-ui/core/Card";
import MuiButton from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";

// icons
import ActionCancel from "@material-ui/icons/Cancel";
import ActionEdit from "@material-ui/icons/Edit";
import ActionSave from "@material-ui/icons/Save";
import CircularProgress from "@material-ui/core/CircularProgress";
import ActionRun from "@material-ui/icons/PlayCircleFilled";

// jazasoft
import {
  NumberInput,
  BooleanInput,
  TextInput,
  SelectInput,
  SelectArrayInput,
  FunctionField,
  WithReduxForm,
  required,
  minValue,
  showNotification,
  translate,
  Button
} from "jazasoft";
import { SimpleForm } from "jazasoft/lib/mui/form/SimpleForm";

import Table, { TextInput as TTextInput } from "../../../components/Table";
import { SpreadingType, FabricDirection, OState, Role, TaskNames } from "../../../utils/types";
import { getDistinctValues, getWeightedMean, inputOptions, isEmpty, isFalse, isTrue, toFixed } from "../../../utils/helpers";
import useDeepMemo from "../../../hooks/useDeepMemo";
import useSafeState from "../../../hooks/useSafeState";
import hasPrivilege from "../../../utils/hasPrivilege";
import {
  PART_SHELL,
  // PROP_SHADE_MODERATE,
  // PROP_SHADE_STRICT,
  MULTI_PART_MANUAL_PLAN,
  MULTI_PART_SEPARATE_BUNDLE,
  LayType,
  LotType,
  CutPlanType,
  MiscType
} from "../../../constants";
import useMutation from "../../../hooks/useMutation";
import { UPDATE_TASK } from "../../../reducers/scheduledTaskReducer";
import { Tab, Tabs } from "@material-ui/core";
import { getCutOrderFlowInfoList, getPartType } from "../../../helpers/orderHelper";
import { ResourceId, Scope } from "../../../utils/privilege";
import { useDeepEffect, useSetting } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import MuiTable from "../../../components/MuiTable";
import PlyLimitDialog from "./dialog/PlyLimitDialog";

// const defaultPlyLimit = "2Way=4; 3Way=6; 4Way=20; 5Way=30; 6Way=40; 7Way=50; 8Way=50";

const CutPercentDialog = ({ open, order, cutOrder, onSubmit, onClose }) => {
  const [rows, setRows] = useSafeState([]);

  const dispatch = useDispatch();
  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  const onSave = () => {
    if (rows.some(e => isEmpty(e.extra))) {
      dispatch(showNotification(`Extra Percent is required.`, { type: "danger" }));
    } else {
      onSubmit && onSubmit(rows);
    }
  };

  const handleClose = () => {
    if (rows.some(e => isEmpty(e.extra))) {
      dispatch(showNotification(`Extra Percent is required.`, { type: "danger" }));
    } else {
      onClose && onClose();
    }
  };

  const onChange =
    ({ rowIdx, column }) =>
    event => {
      const value = event.target.value;
      setRows(rows => {
        let newRows = [...rows];
        let row = { ...rows[rowIdx], [column.dataKey]: value };
        newRows.splice(rowIdx, 1, row);
        return newRows;
      });
    };

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "orderQty", title: "Order Qty" },
      {
        dataKey: "extra",
        title: "Extra Cut(%)",
        element: cutOrder?.stateValue > OState.FINAL_GROUPING_COMPLETED.stateValue ? undefined : <TTextInput onChange={onChange} type="number" />
      }
    ];
    return cols;
  }, [cutOrder]);

  useDeepEffect(() => {
    if (isEmpty(flowInfoList)) return;

    const extraPercentList = cutOrder?.orderConfig?.setting?.extraCutPercentList || [];
    if (isEmpty(extraPercentList)) {
      const rows = cutOrder?.styleColorList?.map(styleColor => {
        const fiList = flowInfoList.filter(e => e.styleColor === styleColor);
        if (isEmpty(fiList)) return {};
        const orderQty = fiList.reduce((acc, e) => acc + (e.orderQty || e.fpoQty), 0);
        const extraData = fiList.map(e => ({ key: e.fpoExtra || 0, weight: e.fpoQty || e.orderQty }));
        const extra = getWeightedMean(extraData);
        return { style: fiList[0].style, color: fiList[0].color, orderQty, extra: toFixed(extra) };
      });
      setRows(rows);
    } else {
      setRows(extraPercentList);
    }
  }, [cutOrder, flowInfoList]);

  return (
    <Dialog id="adjust-dialog" fullWidth={true} maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle id="import-dialog-title">Extra Cut Percent</DialogTitle>
      <Divider />
      <Table columns={columns} rows={rows} />
      {cutOrder?.stateValue <= OState.FINAL_GROUPING_COMPLETED.stateValue && (
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "1.5em" }}>
          <Button label="Cancel" variant="outlined" color="primary" onClick={handleClose} />
          <Button label="Save" variant="contained" color="primary" style={{ marginLeft: "1em" }} onClick={onSave} />
        </div>
      )}
    </Dialog>
  );
};

const choicesReserveType = [
  { id: "Across Groups", name: "Across Groups" },
  { id: "Per Roll (Fixed)", name: "Per Roll (Fixed)" }
];

const advancedSettingKeys = [
  {
    dataKey: "cutConstant",
    label: "Cut Constant [4,5,6]",
    dataType: "select",
    choices: [
      { id: 4, name: "4" },
      { id: 5, name: "5" },
      { id: 6, name: "6" }
    ],
    defaultValue: 6
  },
  {
    dataKey: "wayMaximizeConstant",
    label: "Way Maximization Constant [0, 25, 50, 75]",
    dataType: "number",
    defaultValue: 50
  },
  {
    dataKey: "sizeExhaustConstant",
    label: "Size Exhaustion Constant [0, 25, 50, 75, 100]",
    dataType: "number",
    defaultValue: 50
  },
  {
    dataKey: "sizeMixingConstant",
    label: "Size Mixing Constant [0, 25, 50, 75]",
    dataType: "number",
    defaultValue: 50
  },
  {
    dataKey: "preferHigherSizeConstant",
    label: "Prefer Higher Size Constant [0, 25, 50, 75, 100]",
    dataType: "number",
    defaultValue: 50
  },
  {
    dataKey: "mainWidthReGroupingTolerance",
    label: "Width ReGrouping Tolerance (Main)",
    dataType: "number",
    defaultValue: 2
  },
  {
    dataKey: "remnantWidthReGroupingTolerance",
    label: "Width ReGrouping Tolerance (Remnant)",
    dataType: "number",
    defaultValue: 5
  },
  {
    dataKey: "mainCutOffPercent",
    label: "Cut-Off Percent for Main Marker",
    dataType: "number",
    defaultValue: null
  },
  {
    dataKey: "endBitCsvMultiplier",
    label: "End Bit CSV Multiplier",
    dataType: "number",
    defaultValue: 1.5
  },
  {
    dataKey: "maxRatio",
    label: "Maximum Ratio allowed for any size",
    dataType: "number",
    defaultValue: null
  },
  {
    dataKey: "skipZeroSizeRank",
    label: "Skip Zero Size Rank",
    dataType: "boolean",
    defaultValue: true
  }
];

// const choicesPropShade = [
//   { id: null, name: "None" },
//   { id: PROP_SHADE_MODERATE, name: PROP_SHADE_MODERATE },
//   { id: PROP_SHADE_STRICT, name: PROP_SHADE_STRICT }
// ];

const choicesCutPlanType = [
  { id: CutPlanType.ALGORITHMIC, name: CutPlanType.ALGORITHMIC },
  // { id: CutPlanType.HYBRID, name: CutPlanType.HYBRID },
  { id: CutPlanType.CUSTOM, name: CutPlanType.CUSTOM }
];

const choicesOthers = [
  { id: MiscType.CPL, name: "CPL" },
  { id: MiscType.SPLICING, name: "Splicing" }
  // { id: MiscType.STEP_LAYING, name: "Step Laying" },
];

const getPlyFormatData = rows => {
  if (isEmpty(rows)) return "";
  return rows.map(row => `${row?.way || 0}Way=${row?.value || 0}`).join(";");
};

const useStyles = makeStyles({
  padding: {
    padding: "1.5em"
  },
  settings: {
    padding: "1.5em 1.5em 0.5em 1.5em"
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "0.5rem"
  },
  icon: {
    marginRight: "0.5em"
  }
});

const CutPlanSettings = props => {
  const classes = useStyles();
  const { className, cutOrder, order, scheduledTasks = {}, selectedFlowInfo, onRefresh, roles, hasAccess, translate } = props;

  const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);

  const dispatch = useDispatch();

  //** States **/
  const [tabIdx, setTabIdx] = useSafeState(0);
  const [editing, setEditing] = useSafeState(false);
  const [dialogState, setDialogState] = useSafeState({ active: false });
  const [extraState, setExtraState] = useSafeState({ extraPercent: 0, extraCutPercentList: [] });
  const [plyDialogState, setPlyDialogState] = React.useState({ open: false });
  const [plyRows, setPlyRows] = React.useState([]);

  //** Redux Selector */
  const formValues = useSelector(state => state?.form?.setting?.values || {});
  // const tenantId = useSelector(state => state?.jazasoft?.auth?.tenant?.tenantId);

  //** API calls **//
  const showStdCuVal = useSetting(SettingKeys.SHOW_STD_CU);
  const showStdCu = isTrue(showStdCuVal);
  const mainWidthReGroupingTolerance = useSetting(SettingKeys.MAIN_WIDTH_REGROUPING_TOLERANCE);
  const remnantWidthReGroupingTolerance = useSetting(SettingKeys.REMNANT_WIDTH_REGROUPING_TOLERANCE);
  const endLossGlobalValue = useSetting(SettingKeys.MARKER_ENDLOSS);
  const manualWastageAllowance = useSetting(SettingKeys.WASTAGE_PERCENT);
  // const discardedWays = useSetting(SettingKeys.MARKER_WAYS_DISCARDED);
  const markerPlyLimitVal = useSetting(SettingKeys.MARKER_PLY_LIMIT);

  const maxPieces = formValues?.maxPieces || 0;

  useDeepEffect(() => {
    const markerPlyLimit = !isEmpty(markerPlyLimitVal) ? JSON.parse(markerPlyLimitVal) : [];

    const orderConfig = cutOrder?.orderConfig || {};
    const cutSetting = !isEmpty(orderConfig?.cutSetting) ? JSON.parse(orderConfig.cutSetting) : {};

    const sbList = selectedFlowInfo?.flowInfoList?.flatMap(fi => fi.fpoSizeBreakupList || []) || [];
    const noOfSizes = getDistinctValues(sbList.map(e => e.sizeKey)).length;
    const fpoQty = selectedFlowInfo?.fpoQty || selectedFlowInfo?.orderQty || 0;

    const finalMaxPieces = cutSetting?.maxPieces || maxPieces;

    if (markerPlyLimit?.length > 0 && isEmpty(cutSetting?.plyLimit)) {
      const plyLimit = markerPlyLimit.find(({ orderQty, sizeCount }) => {
        const { from: orderQtyFrom, to: orderQtyTo } = orderQty || {};
        const { from: sizeFrom, to: sizeTo } = sizeCount || {};
        return sizeFrom <= noOfSizes && sizeTo > noOfSizes && orderQtyFrom <= fpoQty && orderQtyTo > fpoQty;
      })?.plyLimit;
      const wayData = plyLimit?.split(";") || [];
      const plyRows = wayData
        .map(el => {
          const value = Number(el.split("=")[1].trim()) || 0;
          const way = Number(el.split("Way")[0].trim()) || 0;
          return { way, value };
        })
        .filter(e => e.way <= finalMaxPieces);

      setPlyRows(plyRows);
    } else {
      const wayData = cutSetting?.plyLimit?.split(";") || [];
      const plyRows = wayData
        .map(el => {
          const value = Number(el.split("=")[1].trim()) || 0;
          const way = Number(el.split("Way")[0].trim()) || 0;
          return { way, value };
        })
        .filter(e => e.way <= finalMaxPieces);
      setPlyRows(plyRows);
    }
  }, [order, cutOrder, markerPlyLimitVal, selectedFlowInfo, maxPieces]);

  const [mutate, { loading: busy }] = useMutation();
  const [mutateSetting, { loading: savingSetting }] = useMutation();

  const layTypeMap = {
    [LayType.ALL_MIX]: translate("resources.rollPlan.fields.setting.layType.all_mix", { _: LayType.ALL_MIX }),
    [LayType.SIZE_GROUP_WISE]: translate("resources.rollPlan.fields.setting.layType.size_group_wise", { _: LayType.SIZE_GROUP_WISE })
  };

  //** Handler Methods**//
  const onCutPlanRun = (handleSubmit, fastPlan) => {
    handleSubmit(values => {
      // const hasProductRatio = flowInfoList.filter(fi => !isEmpty(fi.ratio)).length > 1;
      // const productIds = hasProductRatio ? flowInfoList.filter(fi => !isEmpty(fi.ratio)).map(fi => fi.product?.id) : [orderFlow.product?.id];
      // const flowList = order.orderFlowList
      //   ?.filter(
      //     of =>
      //       of.styles === orderFlow?.styles && of.colors === orderFlow?.colors && productIds.includes(of.product?.id)
      //   )
      //   ?.filter(of => of.stateValue > OState.NEW.stateValue);
      if (isEmpty(values?.maxPieces)) {
        dispatch(showNotification("Max. Garment per Way is required.", { type: "danger" }));
        return;
      }

      if (flowInfoList.some(fi => isEmpty(fi.fpoSizeBreakupList))) {
        dispatch(showNotification("Size Breakup is missing. Hint: Update Size Breakup on order page.", { type: "danger" }));
        return;
      }
      if (!isEmpty(values.reserveType) && isEmpty(values.reserveQty)) {
        dispatch(showNotification("Reservation Qty is required", { type: "danger" }));
        return;
      }
      if (isEmpty(values.reserveType) && !isEmpty(values.reserveQty)) {
        dispatch(showNotification("Reservation Type is required", { type: "danger" }));
        return;
      }

      const isCPL = !isEmpty(values?.others) && Array.isArray(values.others) && values.others.includes(MiscType.CPL);
      if (isCPL) {
        if (isEmpty(values?.cplWarpShrPercent)) {
          dispatch(showNotification("CPL Warp Shrinkage Percent is required.", { type: "danger" }));
          return;
        }
        if (isEmpty(values?.cplWeftShrPercent)) {
          dispatch(showNotification("CPL Warp Shrinkage Percent is required.", { type: "danger" }));
          return;
        }
        if (values?.cplWarpShrPercent > 0 || values?.cplWeftShrPercent > 0) {
          const result = window.confirm("Are you sure your fabric has elongation and not shrinkage?. \n\nFor shrinkage, value should be negative.");
          if (!result) {
            return;
          }
        }
      }

      // const flowIds = isEmpty(flowList) ? [orderFlow?.id] : flowList.map(e => e.id);
      // const noOfLots = flowInfoList.sort((a, b) => b.lots - a.lots)[0]?.lots || 1;
      // const partCount = getDistinctValues(flowInfoList.map(e => e.part)).length;
      const orderQty = flowInfoList?.reduce((acc, e) => acc + (e.fpoQty || e.orderQty), 0);
      const extraCutPercentList =
        extraState?.extraCutPercentList?.length <= 1
          ? [{ style: cutOrder.styles, color: cutOrder?.colors, orderQty, extra: values?.extra, cutAll: values?.cutAll }]
          : extraState?.extraCutPercentList;

      const planningDto = {
        cutPlanSetting: { ...values, plyLimit: getPlyFormatData(plyRows), extraCutPercentList, fastPlan },
        cutSetting: { ...values, plyLimit: getPlyFormatData(plyRows), extraCutPercentList, fastPlan },
        cutOrderId: cutOrder?.id,
        // flowIds: flowIds.join(","),
        // flowInfoId,
        poRef: order?.poRef,
        colors: cutOrder?.colors,
        styles: cutOrder?.styles,
        inseams: cutOrder?.inseams,
        destinations: cutOrder?.destinations,
        deliveries: cutOrder?.deliveries,
        flowRefs: cutOrder?.flowRefs,
        customerFlowRefs: cutOrder?.customerFlowRefs,
        productName: cutOrder?.product?.name,
        part: cutOrder?.part,
        lot: cutOrder?.lot
        // noOfLots,
        // partCount
      };

      const runOptions = {
        url: `orders/${order?.id}/markers`,
        method: "post",
        data: planningDto,
        params: { action: "generate", flowId: cutOrder.id }
      };

      const runCutPlan = () =>
        mutate(runOptions, {
          onSuccess: data => {
            dispatch({ type: UPDATE_TASK, payload: { task: data } });
            dispatch(showNotification("Task queued. you will be notified once completed"));
          }
        });

      // The fast-plan endpoint persists this value as part of its workflow, but
      // the regular Cutplan Options flow only uses the value for generation.
      // Persist it first so order config reflects what the user entered.
      if (!fastPlan) {
        mutateSetting(
          {
            url: `order-flow/${cutOrder?.id}/setting`,
            method: "put",
            params: { action: "cut_plan" },
            data: {
              ...cutOrder,
              orderConfig: {
                ...cutOrder?.orderConfig,
                stdCuAllowance: values?.stdCuAllowance,
                setting: {
                  ...cutOrder?.orderConfig?.setting,
                  ...values,
                  plyLimit: getPlyFormatData(plyRows),
                  extraCutPercentList,
                  stdCuAllowance: values?.stdCuAllowance
                }
              }
            }
          },
          { onSuccess: runCutPlan }
        );
      } else {
        runCutPlan();
      }
    })();
  };

  const onSettingUpdate = handleSubmit => {
    handleSubmit(values => {
      const {
        stdCuAllowance,
        wastagePercent,
        cutPlanType,
        others,
        fabricDirection,
        spreadingType,
        cplWarpShrPercent,
        cplWeftShrPercent,
        endLoss,
        cutPlanRemarks
      } = values;
      const options = {
        url: `order-flow/${cutOrder?.id}/setting`,
        method: "put",
        params: {
          action: "cut_plan"
        },
        data: {
          ...cutOrder,
          orderConfig: {
            ...cutOrder?.orderConfig,
            multiPartBundling: cutPlanType === CutPlanType.CUSTOM ? MULTI_PART_MANUAL_PLAN : MULTI_PART_SEPARATE_BUNDLE,
            stdCuAllowance,
            manualWastageAllowance: cutPlanType === CutPlanType.CUSTOM ? wastagePercent : undefined,
            setting: {
              others,
              multiPartBundling: cutPlanType === CutPlanType.CUSTOM ? MULTI_PART_MANUAL_PLAN : MULTI_PART_SEPARATE_BUNDLE,
              cutPlanType,
              fabricDirection,
              cplWarpShrPercent,
              cplWeftShrPercent,
              endLoss,
              cutPlanRemarks,
              stdCuAllowance,
              spreadingType: spreadingType || SpreadingType.FACE_ONE_WAY
            }
          }
        }
      };

      mutate(options, {
        onSuccess: data => {
          dispatch(showNotification("Cut Plan Setting saved successfully"));
          onRefresh && onRefresh();
          setEditing(false);
        }
      });
    })();
  };

  //** Data Processing **//
  const isPreRun = cutOrder?.lotType === LotType.PRE_RUN;
  const finalGroupingCompleted = cutOrder?.stateValue >= OState.FINAL_GROUPING_COMPLETED.stateValue;
  const cutPlanGenerated =
    cutOrder?.stateValue > OState.FINAL_GROUPING_COMPLETED.stateValue ||
    (cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue && isTrue(cutOrder?.cutOptionsAvailable));
  const feedbackStarted = cutOrder?.stateValue >= OState.MAIN_FEEDBACK_IN_PROGRESS.stateValue;

  const setting = useDeepMemo(() => {
    const legacySetting = isEmpty(cutOrder?.orderConfig?.cutSetting) ? {} : JSON.parse(cutOrder.orderConfig.cutSetting);
    const currentSetting = cutOrder?.orderConfig?.setting;
    const parsedCurrentSetting = typeof currentSetting === "string" ? JSON.parse(currentSetting) : currentSetting || {};
    return { ...legacySetting, ...parsedCurrentSetting };
  }, [cutOrder?.orderConfig]);
  const automatedCutPlan = isEmpty(setting?.cutPlanType)
    ? formValues?.cutPlanType !== CutPlanType.CUSTOM
    : setting?.cutPlanType !== CutPlanType.CUSTOM;
  const customPlan = setting?.cutPlanType === CutPlanType.CUSTOM;
  const isCsv = !isEmpty(cutOrder?.groupList) && cutOrder.groupList.every(g => !isEmpty(g.csv) && g.csv !== "No" && g.csv !== "Grade 4");

  const task = scheduledTasks[cutOrder?.id] || {};
  const generatingCutPlan = task.name === TaskNames.CUT_PLAN_GENERATE && ["New", "Waiting", "Running"].includes(task.state);

  const hasSizeGroup = useDeepMemo(() => {
    if (isEmpty(cutOrder?.flowInfoIds) || isEmpty(order?.flowInfoList)) return false;

    const flowInfoList = getCutOrderFlowInfoList(order, cutOrder);
    const sbMap = flowInfoList.flatMap(fi => fi.fpoSizeBreakupList || []).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sizeBreakupList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    const hasSizeGroup = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 1;
    return hasSizeGroup;
  }, [order, cutOrder]);

  const initialValues = useDeepMemo(() => {
    //** Initialize Default value for advancedSettingKeys **//
    // Ply Limit: Function of order qty
    // Cut-off Percent: Function of order qty
    // End-bit csv multiplier: From Settings

    const orderConfig = cutOrder?.orderConfig || {};
    const cutSetting = !isEmpty(orderConfig?.cutSetting) ? JSON.parse(orderConfig.cutSetting) : {};
    const configuredSetting =
      typeof orderConfig?.setting === "string" ? JSON.parse(orderConfig.setting) : orderConfig?.setting || {};
    const data = !isEmpty(flowInfoList) ? flowInfoList.map(fi => ({ key: fi.fpoExtra || 0, weight: fi.orderQty || 0 })) : {};
    const extra = getWeightedMean(data);

    const values = {
      id: cutOrder?.id,
      ...advancedSettingKeys.reduce((acc, e) => ({ ...acc, [e.dataKey]: e.defaultValue }), {}),
      ...cutSetting,
      ...configuredSetting,
      mainWidthReGroupingTolerance,
      remnantWidthReGroupingTolerance,
      extra: toFixed(extra),
      cutAll: cutSetting.cutAll || false,
      allowOneWayCsv: cutSetting.allowOneWayCsv || false,
      plyPairPerRoll: cutSetting.plyPairPerRoll || false,
      spreadingType: cutSetting.spreadingType || SpreadingType.FACE_ONE_WAY,
      fabricDirection: cutSetting.fabricDirection || "Two Way",
      maxPliesInLay: cutSetting.maxPliesInLay || 150,
      stdCuAllowance: cutOrder?.orderConfig?.stdCuAllowance,
      wastagePercent: isEmpty(cutOrder?.orderConfig)
        ? Number(manualWastageAllowance)
        : !isEmpty(cutOrder?.orderConfig?.manualWastageAllowance)
          ? Number(cutOrder?.orderConfig?.manualWastageAllowance)
          : undefined,
      endLoss: cutSetting?.endLoss || Number(endLossGlobalValue),
      others: cutSetting?.others || [],
      cutPlanType: !isEmpty(cutSetting?.cutPlanType)
        ? cutSetting.cutPlanType
        : !isEmpty(configuredSetting?.cutPlanType)
          ? configuredSetting.cutPlanType
          : typeof cutSetting?.fastPlan === "boolean"
            ? CutPlanType.ALGORITHMIC
            : getPartType(cutOrder?.part)?.toUpperCase() === PART_SHELL.toUpperCase()
              ? CutPlanType.ALGORITHMIC
              : undefined
    };
    return values;
  }, [cutOrder, endLossGlobalValue]);

  useDeepEffect(() => {
    if (isEmpty(flowInfoList)) return;
    const extraPercentList = cutOrder?.orderConfig?.setting?.extraCutPercentList || [];
    if (isEmpty(extraPercentList)) {
      const rows = cutOrder?.styleColorList?.map(styleColor => {
        const fiList = flowInfoList.filter(e => e.styleColor === styleColor);
        if (isEmpty(fiList)) return {};
        const orderQty = fiList.reduce((acc, e) => acc + (e.orderQty || e.fpoQty), 0);
        const extraData = fiList.map(e => ({ key: e.fpoExtra || 0, weight: e.fpoQty || e.orderQty }));
        const extra = getWeightedMean(extraData);
        return { style: fiList[0].style, color: fiList[0].color, orderQty, extra: toFixed(extra) };
      });
      setExtraState({ extraPercent: rows[0]?.extra || 0, extraCutPercentList: rows });
    } else {
      const extraData = extraPercentList.map(e => ({ key: Number(e.extra || 0), weight: e.orderQty || 0 }));
      const extra = getWeightedMean(extraData);
      setExtraState({ extraPercent: toFixed(extra) || 0, extraCutPercentList: extraPercentList });
    }
  }, [cutOrder, flowInfoList]);

  const onExtraPercentSave = flowInfoList => {
    const data = flowInfoList.map(fi => ({ key: fi.extra || 0, weight: fi.orderQty || 0 }));
    const extra = getWeightedMean(data);
    setExtraState({
      extraPercent: toFixed(extra),
      extraCutPercentList: flowInfoList
    });
    setDialogState({ open: false });
  };

  const hasSettingUpdateAccess = hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER_SETTING, Scope.UPDATE);
  const isSuperUser = !isEmpty(roles) && (roles.includes(Role.MASTER) || roles.includes(Role.SUPER_USER));
  const isAdmin = !isEmpty(roles) && roles.includes(Role.ADMIN_USER);

  const showFooter =
    (hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER_SETTING, Scope.UPDATE) &&
      cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue &&
      isFalse(cutOrder?.cutOptionsAvailable)) ||
    !feedbackStarted;

  // const choicesLines = isEmpty(lines) ? [] : listFromCsv(lines).map(e => ({ id: e, name: e }));
  const distinctParts = getDistinctValues(
    flowInfoList
      ?.filter(fi => cutOrder.productIdList?.includes(fi.product?.id))
      .flatMap(e => e.partList || [])
      .map(p => p.effPartName)
  );
  const allowCustomPlan =
    isSuperUser ||
    isAdmin ||
    cutOrder?.lotType === LotType.PRE_RUN ||
    (distinctParts?.length > 1 && getPartType(cutOrder?.part)?.toUpperCase() !== PART_SHELL.toUpperCase());

  const advSettingKeys =
    roles?.includes(Role.SUPER_USER) || roles?.includes(Role.ADMIN_USER) || roles?.includes(Role.MASTER) ? advancedSettingKeys : [];

  const disabled = customPlan ? !isEmpty(cutOrder?.markerList) && editing : editing;
  const disabledAfterRollPlan = cutOrder?.stateValue >= OState.ROLL_PLAN_GENERATED.stateValue;

  const tabList = useDeepMemo(() => {
    if (isPreRun || formValues.cutPlanType === CutPlanType.CUSTOM) return ["Basic Settings"];
    let tabs = ["Basic Settings", "Advanced Settings"];
    if (isEmpty(roles) || !(roles.includes(Role.MASTER) || roles.includes(Role.SUPER_USER) || roles.includes(Role.ADMIN_USER))) {
      tabs = tabs.slice(0, 1);
    }
    return tabs;
  }, [roles, isPreRun, formValues?.cutPlanType]);

  React.useEffect(() => {
    if (tabList.length <= 1) {
      setTabIdx(0);
    }
  }, [tabList, setTabIdx]);

  const settingRecord = { ...cutOrder, ...cutOrder?.orderConfig, ...setting };

  const styleColorList = cutOrder?.styleColorList || [];
  const showExtraAsLink = styleColorList.length > 1 && formValues.cutPlanType !== CutPlanType.CUSTOM;
  const isCustomPlan = formValues.cutPlanType === CutPlanType.CUSTOM;

  let columnList = [];
  if (tabIdx === 0) {
    columnList = [
      { dataKey: "cutPlanType", title: "Cut Plan Type", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "maxPieces", title: "Max Pieces", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "fabricDirection", title: "Fabric Direction", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "discardedWays", title: "Discarded Ways", render: value => (!isEmpty(value) ? value : "-") },
      // { dataKey: "proportionalShade", title: "Proportional Shade", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "reserveType", title: "Recutting \nReserve Method", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "reserveQty", title: "Recutting \nReserve Qty", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "others", title: "Others (CPL, Splicing, etc)", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "layType", title: "Lay Type", render: value => layTypeMap[value] || "" },
      { dataKey: "stdCuAllowance", title: "Std CU Allowance", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "wastagePercent", title: "Wastage Allowance", render: value => (!isEmpty(value) ? value : "-") },
      { dataKey: "allowOneWayCsv", title: "Allow One Way Csv", render: value => (value ? "Yes" : "No") },
      {
        dataKey: "extra",
        title: "Extra Cut Percent",
        render: value => (!isEmpty(value) ? value : "-")
      },
      { dataKey: "cutAll", title: "Cut All", render: value => (value ? "Yes" : "No") },
      {
        dataKey: "cutPercent",
        title: "Extra Cut Percent",
        render: value => {
          return (
            <span style={{ color: "blue" }} onClick={() => setDialogState({ open: true })}>
              {!isEmpty(value) ? value : "-"}
            </span>
          );
        }
      },
      { dataKey: "spreadingType", title: "Spreading Type" },
      { dataKey: "plyPairPerRoll", title: "Ply Pair Per Roll", render: value => (value ? "Yes" : "No") },
      { dataKey: "cplWarpShrPercent", title: "CPL Warp Shrinkage %", render: value => (!isEmpty(value) ? `${value} %` : "") },
      { dataKey: "cplWeftShrPercent", title: "CPL Weft Shrinkage %", render: value => (!isEmpty(value) ? `${value} %` : "") },
      { dataKey: "cutPlanRemarks", title: "Cut Plan Remarks", render: value => (!isEmpty(value) ? value : "-") }
    ];

    if (!automatedCutPlan) {
      columnList = columnList.filter(
        c =>
          ![
            "maxPieces",
            "discardedWays",
            "line",
            "proportionalShade",
            "reserveType",
            "reserveQty",
            "allowOneWayCsv",
            "extra",
            "cutAll",
            "cutPercent"
          ].includes(c.dataKey)
      );
    } else {
      columnList = columnList.filter(c => !["wastagePercent"].includes(c.dataKey));
    }

    if (!hasSizeGroup) {
      columnList = columnList.filter(c => c.dataKey !== "layType");
    }

    if (!showStdCu) {
      columnList = columnList.filter(c => c.dataKey !== "stdCuAllowance");
    }

    if (!isCsv) {
      columnList = columnList.filter(c => c.dataKey !== "allowOneWayCsv");
    }

    if (!automatedCutPlan || showExtraAsLink) {
      columnList = columnList.filter(c => c.dataKey !== "extra" && c.dataKey !== "cutAll");
    }

    if (!automatedCutPlan || !showExtraAsLink) {
      columnList = columnList.filter(c => c.dataKey !== "cutPercent");
    }

    const isCPL = !isEmpty(settingRecord?.others) && Array.isArray(settingRecord.others) && settingRecord.others.includes(MiscType.CPL);
    if (!isCPL) {
      columnList = columnList.filter(c => !["cplWarpShrPercent", "cplWeftShrPercent"].includes(c.dataKey));
    }

    if (!automatedCutPlan) {
      columnList = columnList.filter(c => c.dataKey !== "plyPairPerRoll");
    }

    if (cutOrder?.orderConfig?.spreadingType === SpreadingType.FACE_ONE_WAY) {
      columnList = columnList.filter(c => c.dataKey !== "plyPairPerRoll");
    }
  } else {
    columnList = [
      ...advSettingKeys.map(setting => ({
        dataKey: setting.dataKey,
        title: setting.dataKey.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()),
        render: value => (setting.dataType === "boolean" ? (isTrue(value) ? "Yes" : "No") : !isEmpty(value) ? value : "-")
      })),
      { dataKey: "plyLimit", title: "Ply Limit" }
    ];

    if (allowCustomPlan && formValues.cutPlanType === CutPlanType.CUSTOM) {
      columnList = columnList.filter(c => !advSettingKeys.find(s => s.dataKey === c.dataKey));
    }
  }

  const rows = [
    {
      ...initialValues,
      cutPlanType: initialValues?.cutPlanType || formValues?.cutPlanType,
      extra: extraState?.extraPercent,
      cutPercent: extraState?.extraPercent
    }
  ];

  if (!finalGroupingCompleted && !isPreRun) {
    return <div />;
  }

  return (
    <>
      <CutPercentDialog
        open={dialogState.open}
        order={order}
        cutOrder={cutOrder}
        onSubmit={onExtraPercentSave}
        onClose={() => setDialogState({ open: false })}
      />

      <PlyLimitDialog open={plyDialogState.open} onClose={() => setPlyDialogState({ open: false })} rows={plyRows} setRows={setPlyRows} />
      <WithReduxForm initialValues={initialValues} form="setting">
        {({ handleSubmit }) => (
          <Card className={className}>
            <Tabs value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
              {tabList.map((tab, idx) => (
                <Tab key={idx} label={tab} />
              ))}
            </Tabs>
            <Divider />
            {(((cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue || isPreRun) &&
              isFalse(cutOrder?.cutOptionsAvailable) &&
              !customPlan) ||
              editing) && (
              <SimpleForm component="div" footer={false} className={classes.padding} i18nKey="bulk">
                {tabIdx === 0 && cutOrder?.lotType === LotType.BULK && (
                  <SelectInput
                    source="cutPlanType"
                    label="Cut Plan Type"
                    choices={choicesCutPlanType.filter(c => allowCustomPlan || c.id !== CutPlanType.CUSTOM)}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && cutOrder?.lotType === LotType.PRE_RUN && (
                  <SelectInput
                    source="cutPlanType"
                    choices={choicesCutPlanType.filter(c => c.id === CutPlanType.CUSTOM)}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <NumberInput source="maxPieces" validate={[required(), minValue(1)]} disabled={disabled} {...inputOptions(3)} />
                )}
                {/* {tabIdx === 0  && (
                <NumberInput source="maxPliesInLay" validate={[required(), minValue(1)]} disabled={disabled} {...inputOptions(3)} />
              )} */}
                {tabIdx === 0 && (
                  <SelectInput
                    source="fabricDirection"
                    choices={Object.keys(FabricDirection).map(key => ({
                      id: FabricDirection[key],
                      name: FabricDirection[key]
                    }))}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <TextInput source="discardedWays" disabled={disabled} {...inputOptions(3)} />
                )}
                {/* {tabIdx === 0 && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <SelectInput source="proportionalShade" choices={choicesPropShade} disabled={disabled} {...inputOptions(3)} />
                )} */}
                {tabIdx === 0 && cutOrder?.lotType === LotType.BULK && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <SelectInput
                    label="Recutting Reserve Method"
                    source="reserveType"
                    choices={choicesReserveType}
                    allowEmpty
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && cutOrder?.lotType === LotType.BULK && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <NumberInput label="Recutting Reserve Qty" source="reserveQty" disabled={disabled} {...inputOptions(3)} />
                )}
                {tabIdx === 0 && hasSizeGroup && cutOrder?.lotType === LotType.BULK && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <SelectInput
                    source="layType"
                    choices={[
                      { id: LayType.ALL_MIX, name: translate("resources.rollPlan.fields.setting.layType.all_mix", { _: LayType.ALL_MIX }) },
                      {
                        id: LayType.SIZE_GROUP_WISE,
                        name: translate("resources.rollPlan.fields.setting.layType.size_group_wise", { _: LayType.SIZE_GROUP_WISE })
                      }
                    ]}
                    validate={required()}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && (
                  <NumberInput source="endLoss" validate={[required(), minValue(0)]} disabled={disabledAfterRollPlan} {...inputOptions(3)} />
                )}
                {tabIdx === 0 && showStdCu && (
                  <NumberInput source="stdCuAllowance" validate={[required(), minValue(0)]} disabled={disabledAfterRollPlan} {...inputOptions(3)} />
                )}
                {tabIdx === 0 && formValues.cutPlanType === CutPlanType.CUSTOM && (
                  <NumberInput source="wastagePercent" validate={[required(), minValue(0)]} disabled={disabledAfterRollPlan} {...inputOptions(3)} />
                )}

                {!showExtraAsLink && cutOrder?.lotType === LotType.BULK && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <NumberInput
                    source="extra"
                    label="resources.bulk.fields.extraCutPercent"
                    validate={[required(), minValue(0.0)]}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && (
                  <SelectInput
                    source="spreadingType"
                    choices={Object.keys(SpreadingType).map(key => ({
                      id: SpreadingType[key],
                      name: SpreadingType[key]
                    }))}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}

                {tabIdx === 0 && (
                  <SelectArrayInput
                    source="others"
                    label="Others (CPL, Splicing, etc)"
                    choices={choicesOthers}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && Array.isArray(formValues.others) && formValues.others.includes(MiscType.CPL) && (
                  <NumberInput
                    source="cplWarpShrPercent"
                    label="resources.bulk.fields.cplWarpShrPercent"
                    validate={[required()]}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && Array.isArray(formValues.others) && formValues.others.includes(MiscType.CPL) && (
                  <NumberInput
                    source="cplWeftShrPercent"
                    label="resources.bulk.fields.cplWeftShrPercent"
                    validate={[required()]}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && cutOrder?.lotType === LotType.BULK && formValues.cutPlanType !== CutPlanType.CUSTOM && showExtraAsLink && (
                  <FunctionField
                    source="cutPercent"
                    label="resources.bulk.fields.extraCutPercent"
                    record={{ cutPercent: extraState?.extraPercent || 0 }}
                    xs={12}
                    sm={3}
                    render={record => (
                      <span style={{ color: "blue" }} onClick={() => setDialogState({ open: true })}>
                        {!isEmpty(record) ? `${record.cutPercent}*` : ""}
                      </span>
                    )}
                  />
                )}
                {tabIdx === 0 && (
                  <TextInput
                    source="cutPlanRemarks"
                    label="resources.bulk.fields.cutPlanRemarks"
                    disabled={disabledAfterRollPlan}
                    {...inputOptions(3)}
                  />
                )}
                {tabIdx === 0 && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <FunctionField
                    source="plyLimit"
                    label="Ply Limit"
                    record={{ plyLimit: plyRows || [] }}
                    render={record => (
                      <span
                        style={{
                          width: "110px",
                          color: !disabled ? "blue" : "black",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                          cursor: "pointer"
                        }}
                        onClick={() => (!disabled ? setPlyDialogState({ open: true }) : null)}>
                        {!isEmpty(record?.plyLimit) ? getPlyFormatData(record?.plyLimit) : "0"}
                      </span>
                    )}
                    {...inputOptions(3)}
                  />
                )}
                {!showExtraAsLink && cutOrder?.lotType === LotType.BULK && !isCustomPlan && (
                  <BooleanInput source="cutAll" xs={12} sm={3} options={{ disabled }} />
                )}
                {tabIdx === 0 && isCsv && cutOrder?.lotType === LotType.BULK && !isCustomPlan && (
                  <BooleanInput source="allowOneWayCsv" xs={12} sm={3} options={{ disabled }} />
                )}
                {tabIdx === 0 && formValues.spreadingType !== SpreadingType.FACE_ONE_WAY && (
                  <BooleanInput source="plyPairPerRoll" xs={12} sm={3} options={{ disabled }} />
                )}

                {tabIdx === 1 && formValues.cutPlanType !== CutPlanType.CUSTOM && (
                  <SelectInput
                    source="spreadingType"
                    choices={Object.keys(SpreadingType).map(key => ({
                      id: SpreadingType[key],
                      name: SpreadingType[key]
                    }))}
                    disabled={disabled}
                    {...inputOptions(3)}
                  />
                )}

                {tabIdx === 1 && formValues.spreadingType !== SpreadingType.FACE_ONE_WAY && (
                  <BooleanInput source="plyPairPerRoll" xs={12} sm={3} options={{ disabled }} />
                )}
                {tabIdx === 1 &&
                  formValues.cutPlanType !== CutPlanType.CUSTOM &&
                  advSettingKeys.map(setting =>
                    setting.dataType === "number" ? (
                      <NumberInput key={setting.dataKey} source={setting.dataKey} disabled={disabled} {...inputOptions(3)} />
                    ) : setting.dataType === "select" ? (
                      <SelectInput
                        key={setting.dataKey}
                        source={setting.dataKey}
                        choices={setting.choices}
                        disabled={disabled}
                        {...inputOptions(3)}
                      />
                    ) : setting.dataType === "boolean" ? (
                      <BooleanInput key={setting.dataKey} source={setting.dataKey} xs={12} sm={3} options={{ disabled }} />
                    ) : (
                      <TextInput key={setting.dataKey} source={setting.dataKey} disabled={disabled} {...inputOptions(3)} />
                    )
                  )}
              </SimpleForm>
            )}

            {(cutPlanGenerated || customPlan) && !editing && (
              <MuiTable
                component="div"
                columns={columnList}
                rows={rows}
                fontSize={16}

                // i18nKey="resources.bulk.fields"
              />
            )}
            {/** Footer */}
            {showFooter && <Divider />}
            {showFooter && (
              <div className={classes.footer}>
                {isSuperUser &&
                  hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.WRITE) &&
                  cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue &&
                  isFalse(cutOrder?.cutOptionsAvailable) &&
                  cutOrder?.lotType === LotType.BULK &&
                  formValues.cutPlanType === CutPlanType.ALGORITHMIC && (
                    <MuiButton
                      color="primary"
                      variant="outlined"
                      disabled={busy || savingSetting || generatingCutPlan}
                      onClick={() => onCutPlanRun(handleSubmit, false)}>
                      {busy || savingSetting || generatingCutPlan ? (
                        <CircularProgress size={20} thickness={2} color="secondary" className={classes.icon} />
                      ) : (
                        <ActionRun className={classes.icon} />
                      )}
                      CUTPLAN OPTIONS
                    </MuiButton>
                  )}
                {hasPrivilege(roles, hasAccess, ResourceId.CUTPLAN, Scope.WRITE) &&
                  (cutOrder?.stateValue === OState.FINAL_GROUPING_COMPLETED.stateValue || isPreRun) &&
                  isFalse(cutOrder?.cutOptionsAvailable) &&
                  !customPlan && (
                    <MuiButton
                      style={{ marginLeft: "1em" }}
                      color="primary"
                      variant="contained"
                      disabled={busy || savingSetting || generatingCutPlan}
                      onClick={() =>
                        formValues.cutPlanType !== CutPlanType.ALGORITHMIC ? onSettingUpdate(handleSubmit) : onCutPlanRun(handleSubmit, true)
                      }>
                      {busy || generatingCutPlan ? (
                        <CircularProgress size={20} thickness={2} color="secondary" className={classes.icon} />
                      ) : formValues.cutPlanType !== CutPlanType.ALGORITHMIC || isPreRun ? (
                        <ActionSave className={classes.icon} />
                      ) : (
                        <ActionRun className={classes.icon} />
                      )}
                      {formValues.cutPlanType !== CutPlanType.ALGORITHMIC || isPreRun ? "SAVE" : "FAST CUTPLAN"}
                    </MuiButton>
                  )}
                {(cutPlanGenerated || customPlan) && !feedbackStarted && hasSettingUpdateAccess && (
                  <React.Fragment>
                    {!editing && (
                      <MuiButton color="primary" variant="outlined" onClick={() => setEditing(true)}>
                        <ActionEdit className={classes.icon} />
                        Edit
                      </MuiButton>
                    )}
                    {editing && (
                      <MuiButton color="primary" variant="outlined" style={{ marginRight: "1em" }} onClick={() => setEditing(false)}>
                        <ActionCancel className={classes.icon} /> Cancel
                      </MuiButton>
                    )}
                    {editing && (
                      <MuiButton color="primary" variant="outlined" disabled={busy} onClick={() => onSettingUpdate(handleSubmit)}>
                        {busy ? (
                          <CircularProgress size={20} thickness={2} color="secondary" className={classes.icon} />
                        ) : (
                          <ActionSave className={classes.icon} />
                        )}
                        Save
                      </MuiButton>
                    )}
                  </React.Fragment>
                )}
              </div>
            )}
          </Card>
        )}
      </WithReduxForm>
    </>
  );
};

export default translate(CutPlanSettings);
